"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { fetchHistory } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { FileText, Calendar, ArrowRight, Download } from 'lucide-react';

interface ResumeHistoryItem {
    id: string;
    resume_name: string;
    created_at: string;
    resume_pdf_url: string;
    cover_letter_pdf_url: string;
}

interface RecentResumesProps {
    onResumeClick?: (id: string) => void;
}

export function RecentResumes({ onResumeClick }: RecentResumesProps = {}) {
    const { token } = useAuth();
    const [resumes, setResumes] = useState<ResumeHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            setResumes([]);
            return;
        }
        
        const load = async () => {
            try {
                // Use cache for dashboard (first page)
                const data = await fetchHistory(1, 5, token, true);
                setResumes(data.data);
            } catch (e) {
                console.error("Failed to load history", e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    if (loading) return <></>;
    if (resumes.length === 0) return null;

    return (
        <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl text-slate-900">Recent Resumes</h2>
                <Link href="/history" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    View All <ArrowRight size={16} />
                </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resumes.map((item) => (
                    <div 
                        key={item.id} 
                        className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-blue-300"
                        onClick={() => onResumeClick?.(item.id)}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                <FileText size={24} />
                            </div>
                            <span className="text-xs text-slate-500 flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-full">
                                <Calendar size={12} />
                                {new Date(item.created_at).toLocaleDateString()}
                            </span>
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 mb-1 truncate" title={item.resume_name}>
                            {item.resume_name}
                        </h3>
                        
                        <div className="flex gap-2 text-sm">
                            <a 
                                href={item.resume_pdf_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                            >
                                <Download size={14} /> Resume
                            </a>
                            <a 
                                href={item.cover_letter_pdf_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                            >
                                <Download size={14} /> Letter
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
